import Popup from '../components/Popup.js';
import { IMAGE_PATHS } from '/settings.js';
import { getText, getCurrentLanguage } from '../translations/translations.js';
import { parseUrlParams, parseUrlForExploreAreaPackTag, parseUrlForDiscover } from '../utils/url-utils.js';
import { createIconElement } from './icon.js';
import { getAreaBySlug } from '../area-selector/index.js';
import { getSpots } from '../map/map.js';

function buildShareUrlForSpot(spotId) {
    if (!spotId) {
        return window.location.href;
    }

    const { areaSlug } = parseUrlParams();
    const basePath = areaSlug ? `/${encodeURIComponent(areaSlug)}/` : '/';
    const url = new URL(basePath, window.location.origin);
    url.searchParams.set('id', spotId);
    return url.toString();
}

/**
 * Builds custom share text with contextual information based on current mode
 * @param {string} shareUrl - The URL to share
 * @param {string} areaSlug - Area slug (optional)
 * @returns {Promise<string>} Formatted share text
 */
async function buildShareText(shareUrl, areaSlug = null, isSingleSpot = false) {
    const lang = getCurrentLanguage();
    let areaName = '';
    let areaId = null;
    let area = null;

    // Get area data (we need both name and ID)
    if (areaSlug) {
        try {
            area = await getAreaBySlug(areaSlug);
            if (area) {
                areaName = area[`name_${lang}`] || area.name_en || areaSlug;
                areaId = area.id;
            } else {
                areaName = areaSlug;
            }
        } catch (error) {
            areaName = areaSlug;
        }
    }

    const areaNameFinal = areaName || 'the Canary Islands';

    // Detect current mode and id from URL
    const { mode, id } = parseUrlParams();

    // Favorites mode
    if (mode === 'favorites' || mode === 'myfavorites') {
        const template = await getText('share.messageFavorites');
        return template.replace('{area}', areaNameFinal).replace('{url}', shareUrl);
    }

    // Explore mode - get tag names
    const exploreTagNames = await getExploreTagNames(areaSlug, area);
    if (exploreTagNames) {
        const template = await getText('share.messageTagMap');
        if (template && template !== 'share.messageTagMap') {
            return template.replace('{tags}', exploreTagNames).replace('{area}', areaNameFinal).replace('{url}', shareUrl);
        }
        const fallback = await getText('share.messageExplore');
        return fallback.replace('{tags}', exploreTagNames).replace('{area}', areaNameFinal).replace('{url}', shareUrl);
    }

    // Map/Discover mode - get map name
    const discoverParams = parseUrlForDiscover();
    if (discoverParams && discoverParams.mapSlug && areaId) {
        try {
            const { fetchWithCache } = await import('./cache/index.js');
            const MAPS_API = '/app_core/api/sqlite/get-maps.php';
            // Fetch maps with area_id parameter (required by API)
            const maps = await fetchWithCache(`${MAPS_API}?area_id=${areaId}`);
            if (Array.isArray(maps)) {
                const map = maps.find(m => m.slug === discoverParams.mapSlug);
                if (map) {
                    const mapName = map[`name_${lang}`] || map.name_en || discoverParams.mapSlug;
                    const template = await getText('share.messageMap');
                    return template.replace('{mapName}', mapName).replace('{area}', areaNameFinal).replace('{url}', shareUrl);
                }
            }
        } catch (error) {
            // Fallback to default
        }
    }

    // Single spot share (explicit flag or id param present and not an array)
    if (isSingleSpot === true || (id && typeof id === 'string' && !id.includes(','))) {
        const template = await getText('share.messageSingle');
        return template.replace('{area}', areaNameFinal).replace('{url}', shareUrl);
    }

    // Fallback: default message (multiple spots)
    const template = await getText('share.message');
    return template.replace('{area}', areaNameFinal).replace('{url}', shareUrl);
}

async function getExploreTagNames(areaSlug, area = null) {
    const exploreParams = await parseUrlForExploreAreaPackTag();
    if (!exploreParams || !exploreParams.tags || exploreParams.tags.length === 0 || exploreParams.tags[0] === 'all') {
        return '';
    }
    try {
        const lang = getCurrentLanguage();
        const { getAreasList, getPacksForArea, getTagsForAreaPack } = await import('./spot-queries.js');
        // Use area from getAreaBySlug if available, otherwise fetch from list
        let areaForExplore = area;
        if (!areaForExplore && areaSlug) {
            const areas = await getAreasList();
            areaForExplore = areas.find(a => a.slug === areaSlug);
        }
        if (!areaForExplore) return '';

        const packs = await getPacksForArea(areaForExplore.id);
        const pack = packs.find(p => p.slug === exploreParams.pack);
        if (!pack) return '';

        const tags = await getTagsForAreaPack(areaForExplore.id, pack.id);
        const selectedTags = tags.filter(t => exploreParams.tags.includes(t.slug));
        if (selectedTags.length === 0) return '';

        return selectedTags.map(t => t[`name_${lang}`] || t.name_en || t.slug).join(', ');
    } catch (error) {
        return '';
    }
}

export async function openSharePopup(shareInput, context = 'spot') {

    const isObjectInput = shareInput && typeof shareInput === 'object';
    const spotId = isObjectInput ? shareInput.spotId : null;
    const spotTitle = isObjectInput ? (shareInput.spotTitle || '') : '';

    let shareUrl = isObjectInput ? (shareInput.shareUrl || '') : (shareInput || '');
    if (!shareUrl && spotId) {
        shareUrl = buildShareUrlForSpot(spotId);
    }
    if (!shareUrl) {
        shareUrl = window.location.href;
    }

    const popupContent = document.createElement('div');
    popupContent.className = 'share-popup-content';

    const updateIconHref = (iconEl, iconId) => {
        const use = iconEl.querySelector('use');
        if (!use) return;
        const href = `/app_core/src/images/icons/icons.svg#${iconId}`;
        use.setAttribute('href', href);
        use.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', href);
    };

    const createButton = (id, title, iconName, onClick) => {
        const btn = document.createElement('button');
        btn.className = `share-btn icon-btn share-btn-${id}`;
        btn.title = title;

        const icon = createIconElement(iconName, 'icon');
        btn.appendChild(icon);

        btn.onclick = onClick;
        return btn;
    };

    // Get area slug for share text
    const { areaSlug } = parseUrlParams();

    // Create popup instance first so we can update footer
    let title;
    let footer = '';
    if (context === 'spot') {
        title = await getText('share.title.spot');
        if (spotTitle) {
            footer = await getText('share.title.spotWithName');
            footer = footer.replace('{title}', spotTitle);
        }
    } else {
        const titleKey = `share.title.${context}`;
        title = await getText(titleKey);
    }
    const popupInstance = Popup({ title, content: popupContent, footer, size: 'extra-small' });

    // Helper function to close popup after 2 seconds
    const closePopupAfterDelay = () => {
        setTimeout(() => {
            popupInstance.close();
        }, 2000);
    };

    // Localized button titles
    const [copyTitle, waTitle, emailTitle, tgTitle, xTitle] = await Promise.all([
        getText('share.btnCopy'),
        getText('share.btnWhatsApp'),
        getText('share.btnEmail'),
        getText('share.btnTelegram'),
        getText('share.btnX')
    ]);

    // Copy Link button
    const copyBtn = createButton('copy', copyTitle || 'Copy Link', 'link-solid', async () => {
        let copySuccess = false;
        // Try modern clipboard API first
        if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
            try {
                await navigator.clipboard.writeText(shareUrl);
                copySuccess = true;
            } catch (err) {
                // Fallback below
            }
        }
        // Fallback: execCommand('copy')
        if (!copySuccess) {
            try {
                const tempInput = document.createElement('input');
                tempInput.value = shareUrl;
                document.body.appendChild(tempInput);
                tempInput.select();
                document.execCommand('copy');
                document.body.removeChild(tempInput);
                copySuccess = true;
            } catch (err) {
                copySuccess = false;
            }
        }
        // Show feedback
        if (copySuccess) {
            copyBtn.classList.add('copied');
            const linkCopiedText = await getText('share.linkCopied');
            const footerEl = popupInstance.element.querySelector('.popup-footer');
            if (footerEl) {
                footerEl.classList.add('link-copied');
            }
            popupInstance.updateContent({ footer: linkCopiedText });
            setTimeout(() => {
                copyBtn.classList.remove('copied');
                if (footerEl) {
                    footerEl.classList.remove('link-copied');
                }
                popupInstance.updateContent({ footer: '' });
            }, 2000);
            closePopupAfterDelay();
        } else {
            // Show error message if copy failed
            const errorText = await getText('share.linkCopied') || 'Copy failed';
            popupInstance.updateContent({ footer: errorText });
        }
    });

    // WhatsApp button
    const waBtn = createButton('wa', waTitle || 'Share on WhatsApp', 'whatsapp-brands', async () => {
        const isSingleSpot = context === 'spot';
        const shareText = await buildShareText(shareUrl, areaSlug, isSingleSpot);
        window.open(`https://wa.me/?text=${encodeURIComponent(shareText)}`, '_blank', 'noopener');
        // Close popup after 2 seconds
        closePopupAfterDelay();
    });

    // Email button
    const emailBtn = createButton('email', emailTitle || 'Share via Email', 'envelope-solid', async () => {
        const isSingleSpot = context === 'spot';
        const shareText = await buildShareText(shareUrl, areaSlug, isSingleSpot);
        const subject = await getText('share.emailSubject');
        window.open(`mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(shareText)}`);
        // Close popup after 2 seconds
        closePopupAfterDelay();
    });

    // Telegram button
    const tgBtn = createButton('tg', tgTitle || 'Share on Telegram', 'telegram-brands', async () => {
        const isSingleSpot = context === 'spot';
        const shareText = await buildShareText(shareUrl, areaSlug, isSingleSpot);
        window.open(`https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`, '_blank', 'noopener');
        // Close popup after 2 seconds
        closePopupAfterDelay();
    });

    // X (Twitter) button
    const xBtn = createButton('x', xTitle || 'Share on X', 'x-twitter-brands-solid-full', async () => {
        const isSingleSpot = context === 'spot';
        // Prefer a dedicated broadcast-style template for X when available
        let shareText = await getText('share.messageX');
        let usedTemplate = false;
        if (!shareText || shareText === 'share.messageX') {
            // Fallback to contextual share text
            shareText = await buildShareText(shareUrl, areaSlug, isSingleSpot);
            // Defensive: remove common personal greetings so X uses a broadcast style
            shareText = shareText.replace(/^(Hi!|Hello!|¡Hola!|Hola!)\s*/i, '');
        } else {
            // Replace placeholder {area} in the social template (we don't expect {url} here)
            let areaName = 'the Canary Islands';
            if (areaSlug) {
                try {
                    const area = await getAreaBySlug(areaSlug);
                    if (area) {
                        const lang = getCurrentLanguage();
                        areaName = area[`name_${lang}`] || area.name_en || areaSlug;
                    } else {
                        areaName = areaSlug;
                    }
                } catch (e) {
                    areaName = areaSlug;
                }
            }
            shareText = shareText.replace('{area}', areaName);
            usedTemplate = true;
        }

        // Build intent URL; avoid duplicating the URL if the text already contains one
        const encodedText = encodeURIComponent(shareText);
        let intentUrl = `https://twitter.com/intent/tweet?text=${encodedText}`;
        const containsUrl = /https?:\/\//.test(shareText) || (shareUrl && shareText.includes(shareUrl));
        if (!containsUrl && shareUrl) {
            intentUrl += `&url=${encodeURIComponent(shareUrl)}`;
        }
        window.open(intentUrl, '_blank', 'noopener');
        closePopupAfterDelay();
    });

    popupContent.append(copyBtn, waBtn, emailBtn, tgBtn, xBtn);
}

export async function openSmartSharePopup(shareUrl) {
    const popupContent = document.createElement('div');
    popupContent.className = 'share-popup-content';

    const updateIconHref = (iconEl, iconId) => {
        const use = iconEl.querySelector('use');
        if (!use) return;
        const href = `/app_core/src/images/icons/icons.svg#${iconId}`;
        use.setAttribute('href', href);
        use.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', href);
    };

    // Check if there are spots visible on the map
    const visibleSpots = getSpots();
    const hasSpotsOnMap = visibleSpots && visibleSpots.length > 0;

    if (!hasSpotsOnMap) {
        // No spots on map - show message
        const messageDiv = document.createElement('div');
        messageDiv.className = 'no-spots-message';

        const icon = createIconElement('share-nodes-solid', 'no-spots-icon');

        const text = document.createElement('p');
        text.className = 'no-spots-text';
        text.textContent = await getText('share.smart.noSpotsOnMap');

        messageDiv.appendChild(icon);
        messageDiv.appendChild(text);
        popupContent.appendChild(messageDiv);

        const title = await getText('share.smart.title');
        Popup({ title, content: popupContent, size: 'extra-small' });
        return;
    }

    // Has spots to share - show sharing options
    const createButton = (id, title, iconName, onClick) => {
        const btn = document.createElement('button');
        btn.className = `share-btn icon-btn share-btn-${id}`;
        btn.title = title;

        const icon = createIconElement(iconName, 'icon');
        btn.appendChild(icon);

        btn.onclick = onClick;
        return btn;
    };

    // Get area slug for share text
    const { areaSlug, mode } = parseUrlParams();

    // Get spot count
    const spotCount = visibleSpots.length;

    // Simple title: "Share spots"
    const title = await getText('share.smart.title');

    // Footer with spot count: "Share {count} spots visible on map"
    let footerText;

    // Check if we're in favorites mode
    if (mode === 'favorites' || mode === 'myfavorites') {
        footerText = await getText('share.smart.footerFavorites');
        if (!footerText || footerText === 'share.smart.footerFavorites') {
            footerText = 'Share your favorite spots';
        }
    } else {
        const exploreTagNames = await getExploreTagNames(areaSlug);
        if (exploreTagNames) {
            let areaName = 'the Canary Islands';
            if (areaSlug) {
                try {
                    const area = await getAreaBySlug(areaSlug);
                    if (area) {
                        const lang = getCurrentLanguage();
                        areaName = area[`name_${lang}`] || area.name_en || areaSlug;
                    } else {
                        areaName = areaSlug;
                    }
                } catch (e) {
                    areaName = areaSlug;
                }
            }
            const footerTemplate = await getText('share.smart.footerTagMap');
            const safeTemplate = (footerTemplate && footerTemplate !== 'share.smart.footerTagMap')
                ? footerTemplate
                : 'Share {tags} map in {area}';
            footerText = safeTemplate
                .replace('{tags}', exploreTagNames)
                .replace('{area}', areaName);
        } else if (spotCount === 1) {
            const footerTemplate = await getText('share.smart.footerSingle');
            footerText = footerTemplate || `Share ${spotCount} spot visible on map`;
        } else {
            const footerTemplate = await getText('share.smart.footerMultiple');
            footerText = footerTemplate.replace('{count}', spotCount.toString());
        }
    }

    // Create popup instance with title and footer
    const popupInstance = Popup({ title, content: popupContent, footer: footerText, size: 'extra-small' });

    // Helper function to close popup after 2 seconds
    const closePopupAfterDelay = () => {
        setTimeout(() => {
            popupInstance.close();
        }, 2000);
    };

    // Copy Link button
    const copyBtn = createButton('copy', 'Copy Link', 'link-solid', async () => {
        navigator.clipboard.writeText(shareUrl);
        // Just add copied class - icon stays the same (black)
        copyBtn.classList.add('copied');

        // Show footer message
        const linkCopiedText = await getText('share.linkCopied');
        const footerEl = popupInstance.element.querySelector('.popup-footer');
        if (footerEl) {
            footerEl.classList.add('link-copied');
        }
        popupInstance.updateContent({ footer: linkCopiedText });

        setTimeout(() => {
            copyBtn.classList.remove('copied');
            // Restore footer message and remove class
            if (footerEl) {
                footerEl.classList.remove('link-copied');
            }
            popupInstance.updateContent({ footer: footerText });
        }, 2000);

        // Close popup after 2 seconds
        closePopupAfterDelay();
    });

    // WhatsApp button
    const isSingleSpotSmart = spotCount === 1;
    const waBtn = createButton('wa', 'Share on WhatsApp', 'whatsapp-brands', async () => {
        const shareText = await buildShareText(shareUrl, areaSlug, isSingleSpotSmart);
        window.open(`https://wa.me/?text=${encodeURIComponent(shareText)}`, '_blank', 'noopener');
        // Close popup after 2 seconds
        closePopupAfterDelay();
    });

    // Email button
    const emailBtn = createButton('email', 'Share via Email', 'envelope-solid', async () => {
        const shareText = await buildShareText(shareUrl, areaSlug, isSingleSpotSmart);
        const subject = await getText('share.emailSubject');
        window.open(`mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(shareText)}`);
        // Close popup after 2 seconds
        closePopupAfterDelay();
    });

    // Localized button titles for smart popup
    const [copyTitle, waTitle, emailTitle, tgTitle, xTitle] = await Promise.all([
        getText('share.btnCopy'),
        getText('share.btnWhatsApp'),
        getText('share.btnEmail'),
        getText('share.btnTelegram'),
        getText('share.btnX')
    ]);

    // Telegram button
    const tgBtn = createButton('tg', tgTitle || 'Share on Telegram', 'telegram-brands', async () => {
        const shareText = await buildShareText(shareUrl, areaSlug, isSingleSpotSmart);
        window.open(`https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`, '_blank', 'noopener');
        // Close popup after 2 seconds
        closePopupAfterDelay();
    });

    // X (Twitter) button
    const xBtn = createButton('x', xTitle || 'Share on X', 'x-twitter-brands-solid-full', async () => {
        const shareText = await buildShareText(shareUrl, areaSlug, isSingleSpotSmart);
        const encodedText = encodeURIComponent(shareText);
        let intentUrl = `https://twitter.com/intent/tweet?text=${encodedText}`;
        const containsUrl = /https?:\/\//.test(shareText) || (shareUrl && shareText.includes(shareUrl));
        if (!containsUrl && shareUrl) {
            intentUrl += `&url=${encodeURIComponent(shareUrl)}`;
        }
        window.open(intentUrl, '_blank', 'noopener');
        closePopupAfterDelay();
    });

    popupContent.append(copyBtn, waBtn, emailBtn, tgBtn, xBtn);
} 